import React from 'react';
import * as lineIcons from './icons/untitled-ui/line-icons';
import * as solidIcons from './icons/untitled-ui/solid-icons';

const DEFAULT_STROKE_WIDTH = 1.5;

const Icons: Record<'line' | 'solid', any> = {
  line: lineIcons,
  solid: solidIcons,
};

type LineIconNames = keyof typeof lineIcons;
type SolidIconNames = keyof typeof solidIcons;

export type UntitledUIIconName = LineIconNames | SolidIconNames;

export interface IconPropsBase extends React.SVGAttributes<SVGSVGElement> {}

type IconTypesMap = {
  [K in UntitledUIIconName]: K extends SolidIconNames
    ? K extends LineIconNames
      ? 'solid' | 'line'
      : 'solid'
    : 'line';
};

export type IconProps<T extends UntitledUIIconName> = {
  name: T;
  type?: IconTypesMap[T];
} & IconPropsBase &
  Omit<Parameters<(typeof Icons)['line'][T]>[0], 'name'>;

function Icon<T extends UntitledUIIconName>({
  name,
  type = 'line' as IconTypesMap[T],
  ...props
}: IconProps<T>): React.ReactElement | null {
  const iconName = Icons[type][name];
  if (!iconName) {
    if (process.env.NODE_ENV === 'development') {
      throw new Error(`Icon not found: ${type}/${name}`);
    } else {
      console.error(`Icon not found: ${type}/${name}`);
    }
    return null;
  }
  return React.createElement(Icons[type][name], {
    strokeWidth: DEFAULT_STROKE_WIDTH,
    ...props,
  });
}

const iconNames = Array.from(
  new Set([...Object.keys(Icons.solid), ...Object.keys(Icons.line)])
) as Array<UntitledUIIconName>;

const MappedIcons = iconNames.reduce(
  (iconMap, iconName) => {
    iconMap[iconName] = props => <Icon {...props} name={iconName} />;
    return iconMap;
  },
  {} as Record<
    UntitledUIIconName,
    (props: Omit<IconProps<UntitledUIIconName>, 'name'>) => React.ReactElement
  >
);

export default Object.assign(Icon, MappedIcons);
